import { type JSX, lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import type { To } from 'react-router';
import {
  createBrowserRouter,
  createRoutesFromElements,
  matchRoutes,
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
} from 'react-router';
// The RouterProvider from the /dom subpath wires up ReactDOM.flushSync; the
// core one silently ignores the `flushSync` navigate option, which the
// environment controller relies on to paint env changes synchronously.
import { RouterProvider } from 'react-router/dom';
import { datadogRum } from '@datadog/browser-rum-slim';
import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { useTrackClientSidePageViews } from '@gonfalon/analytics/useTrackClientSidePageViews';
import { profile } from '@gonfalon/bootstrap-data/bootstrapData';
import { skipSettingsHeadingRevalidation } from '@gonfalon/chrome/settings-layout';
import { useTrackSettingsSession } from '@gonfalon/chrome/settings-navigation';
import { CommandPaletteProvider } from '@gonfalon/command-palette/provider';
import { clientSideId, dogfoodBaseUri, mustCompleteWelcomeSurvey } from '@gonfalon/constants/constants';
import { eventInterceptionPlugin, flagOverridePlugin } from '@gonfalon/dogfood-client/plugins';
import {
  commandPaletteSnapPrefix,
  enableCommandPalette,
  enableLanguagePicker,
  enableManuallyTrackedDdrumViews,
  enablePerceivableDisabledTooltipTriggers,
  enforceEmailVerificationLandingPage,
  spaPageViewsForSegmentCom,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { NoisyErrorBoundary } from '@gonfalon/error-boundaries/NoisyErrorBoundary';
import { useVersionAwareNavigate } from '@gonfalon/frontend-version/navigate';
import { shouldReloadForNewVersion } from '@gonfalon/frontend-version/polling';
import { KeyboardShortcutsProvider, useKeyboardShortcutsInstance } from '@gonfalon/keyboard-shortcuts/react';
import { KeyboardShortcutDialogProvider } from '@gonfalon/keyboard-shortcuts/ui';
import { isEligibleWelcomeSurveyMember } from '@gonfalon/members/isEligibleWelcomeSurveyMember';
import { LocaleProvider } from '@gonfalon/messages/LocaleProvider';
import { matchNewHires } from '@gonfalon/navigator/matchers';
import { toVerifyEmail, toWelcomeSurvey } from '@gonfalon/navigator/navigators';
// The only modules allowed to consume the provider are application entry points.
import { ReactQueryClientProvider } from '@gonfalon/react-query-client/ReactQueryClientProvider';
import { RoutePatternContext } from '@gonfalon/router/RoutePatternContext';
import { generateMatchingAppPathPattern, useMatchingAppPathPattern } from '@gonfalon/router/useMatchingPathPattern';
import { setRouteContext } from '@gonfalon/telemetry/setRouteContext';
import { type RouteViewSpan, startRouteViewSpan } from '@gonfalon/telemetry/startRouteViewSpan';
import { trackError } from '@gonfalon/telemetry/trackError';
import { ThemeProvider } from '@gonfalon/theme/ThemeProvider';
import { useLaunchDarklyToolbar } from '@launchdarkly/toolbar';
import {
  DisabledTooltipTriggerContext,
  RouterProvider as ReactAriaRouterProvider,
  useHref,
} from '@launchpad-ui/components';
import type { Store } from 'redux';

import { updateLocation } from '#actions/router';
import { IconProvider } from '#components/IconProvider';
import { useSdkNotification } from '#components/SdkNotification/useSdkNotification';
import { useDispatch } from '#hooks/useDispatch';

import { appRouteOwnership } from '#routes/appRouteOwnership';
import { appRoutes } from '#routes/appRoutes';

const bundleName = window.__bundle__;

const trackCommandPaletteEvent = createTrackerForCategory('Command Palette Event');

type AppProvidersProps = { reduxStore: Store };

declare global {
  var __DEV__: boolean;
  var __LOCAL_MODE__: boolean;
}

function getRoutelessComponentForBundle(_bundleName: string) {
  switch (_bundleName) {
    case 'oauth2Authorize':
      return lazy(() =>
        import(/* webpackChunkName: "oauth2Authorize.entry" */ '#components/OAuth2AuthorizeContainer').then((mod) => ({
          default: mod.OAuth2AuthorizeContainer,
        })),
      );
    case 'oauthProviderConfig':
      return lazy(() =>
        import(/* webpackChunkName: "oauthProviderConfig.entry" */ '#components/OAuthProviderConfig').then((mod) => ({
          default: mod.OAuthProviderConfig,
        })),
      );
    default:
      return;
  }
}

const createRouter = () => {
  const routes = appRoutes();

  // We have several concerns that need to be managed here:
  // - update Redux state when the route changes (LocationStateWithLayoutEffect)
  // - set up a RoutePatternContext.Provider that provides generic information about the current route for analytics
  //   and our Datadog configurations (RoutePatternProvider)
  // - choose a routeless bundle component if a window.__bundle__ was set (AppLayoutRoute)
  // - navigate to email verification if the user is unverified and the `enforce-email-verification-landing-page` flag
  //   is on (AppLayoutRoute)
  //
  // ...all of these concerns require being within a router context - and RoutePatternProvider needs a static list of
  // route definitions to function properly - so we wrap the actual route definitions in a layout route, which in turn
  // composes these pieces together and threads the required props down.
  const router = createBrowserRouter(
    skipSettingsHeadingRevalidation(
      createRoutesFromElements(
        <Route element={<AppLayoutRoute routes={routes} />} hydrateFallbackElement={null}>
          {routes}
        </Route>,
      ),
    ),
  );

  const initialMatches = matchRoutes(router.routes, router.state.location);
  const initialPathPattern = generateMatchingAppPathPattern(initialMatches, {});
  if (initialPathPattern) {
    setRouteContext(initialPathPattern, appRouteOwnership[initialPathPattern]);
  }

  if (enableManuallyTrackedDdrumViews()) {
    let currentRouteViewSpan: RouteViewSpan | undefined;
    //TODO: replace when soft navigations are added to core web vitals: https://developer.chrome.com/docs/web-platform/soft-navigations-experiment
    router.subscribe(({ location, navigation }) => {
      //A new view will start for every pathname change. This does not include param changes. If we want to create new views for param changes, we need to compare location.key instead.
      if (navigation.state === 'loading' && location.pathname !== navigation.location.pathname) {
        // Per react-router v7, `state.location` reflects the old/source location
        // during a navigation; `navigation.location` is the destination. Match
        // the destination so pathPattern/owner describe the page being loaded
        // — otherwise the LD span and global context are tagged with the page
        // we're leaving.
        const matches = matchRoutes(router.routes, navigation.location);
        const pathPattern = generateMatchingAppPathPattern(matches, {});
        const owner = pathPattern ? appRouteOwnership[pathPattern] : undefined;
        datadogRum.startView({ name: pathPattern ?? navigation.location.pathname });
        // Mirror the DD view split with an LD manual span so LD Observability
        // attributes loading work to the destination route the same way.
        currentRouteViewSpan?.end();
        currentRouteViewSpan = startRouteViewSpan({
          url: navigation.location.pathname,
          pathTemplate: pathPattern ?? undefined,
          owner,
        });
        //The view is starting prior to actual navigation and render of the view, so we need to set the global context earlier.
        if (pathPattern) {
          setRouteContext(pathPattern, owner);
        }
      }
    });
  }

  // When a new frontend version has been deployed (detected by background polling),
  // force a full page reload on the next route change so the user gets the latest code.
  // Only pathname changes trigger this — query param and hash changes are ignored.
  router.subscribe(({ location, navigation }) => {
    if (
      navigation.state === 'loading' &&
      location.pathname !== navigation.location.pathname &&
      shouldReloadForNewVersion()
    ) {
      window.location.href = navigation.location.pathname + navigation.location.search + navigation.location.hash;
    }
  });

  return router;
};

function LocationStateWithLayoutEffect() {
  const location = useLocation();
  const dispatch = useDispatch();

  useLayoutEffect(() => {
    dispatch(updateLocation(location));
  }, [location, dispatch]);

  return null;
}

function SdkNotification() {
  useSdkNotification();

  return null;
}

function RoutePatternProvider(props: { routes: JSX.Element }) {
  const pathPattern = useMatchingAppPathPattern(props.routes);
  useTrackSettingsSession(pathPattern);

  useEffect(() => {
    if (pathPattern) {
      const owner = appRouteOwnership[pathPattern];
      setRouteContext(pathPattern, owner);
    }
  }, [pathPattern]);

  return (
    <RoutePatternContext.Provider value={pathPattern}>
      <Outlet />
    </RoutePatternContext.Provider>
  );
}

function AppLayoutRoute(props: { routes: JSX.Element }) {
  // We combined the newHires entrypoint with the app entrypoint and as a
  // result need to allow an exception for visiting the new hires route without
  // boostrap data for the profile. For all other routes, redirect to email
  // verification if profile data is missing and the
  // `enforce-email-verification-landing-page` flag is on.
  const location = useLocation();
  const isNewHiresRoute = matchNewHires(location);
  const isWelcomeSurveyRoute = location.pathname === '/welcome-survey';
  const navigate = useVersionAwareNavigate(useNavigate(), location.pathname);

  const keyboardShortcuts = useKeyboardShortcutsInstance({
    configure: (instance) => {
      if (enableCommandPalette()) {
        instance.enableScope('command-palette');
      }
    },
  });

  useTrackClientSidePageViews({ routes: props.routes, enabled: spaPageViewsForSegmentCom() });

  // BIS-2536: OAuth members are already authenticated by their provider (Google, GitHub, etc.),
  // so skip the email verification redirect for them. This mirrors the server-side oauthBypass
  // in internal/site/redirect.go:redirectToEmailVerify.
  const memberProfile = profile();
  const oauthBypass = (memberProfile?.oauthProviders?.length ?? 0) > 0;

  if (!isNewHiresRoute && !memberProfile?._verified && !oauthBypass && enforceEmailVerificationLandingPage()) {
    return <Navigate to={toVerifyEmail()} />;
  }

  if (
    !isNewHiresRoute &&
    !isWelcomeSurveyRoute &&
    mustCompleteWelcomeSurvey() &&
    isEligibleWelcomeSurveyMember(memberProfile)
  ) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={toWelcomeSurvey({ search: { returnUrl } })} replace />;
  }

  return (
    <ReactAriaRouterProvider navigate={navigate} useHref={useHref as (href: To) => string}>
      <KeyboardShortcutsProvider instance={keyboardShortcuts}>
        <KeyboardShortcutDialogProvider>
          <CommandPaletteProvider
            snapPrefix={commandPaletteSnapPrefix()}
            onError={(error) => {
              trackError(error, { severity: 'medium', owner: 'team-foundation' });
            }}
            onEvent={(event) => {
              trackCommandPaletteEvent(event.type, event);
            }}
          >
            <NoisyErrorBoundary severity="critical">
              <LocationStateWithLayoutEffect />
              <SdkNotification />
              <RoutePatternProvider routes={props.routes} />
            </NoisyErrorBoundary>
          </CommandPaletteProvider>
        </KeyboardShortcutDialogProvider>
      </KeyboardShortcutsProvider>
    </ReactAriaRouterProvider>
  );
}

function AppProvidersBase(props: AppProvidersProps) {
  let content: JSX.Element;

  const RoutelessContentComponent = getRoutelessComponentForBundle(bundleName);
  if (RoutelessContentComponent !== undefined) {
    content = (
      <Suspense fallback={null}>
        <RoutelessContentComponent />
      </Suspense>
    );
  } else {
    const router = createRouter();
    content = <RouterProvider router={router} />;
  }

  /**
   * Auto-configure toolbar mode: full stack mode runs toolbar in dev server mode,
   * frontend mode runs toolbar in SDK mode.
   *
   * Note: clientSideId will default to `default` for full stack mode to properly
   * connect to the dogfood dev server. If running in front-end only mode, we recommend
   * updating gonfalon.override.conf to set the clientSideId to the actual client side id you want.
   *
   * Example:
   * [dogfood]
   * clientSideId = "your-client-side-id"
   */
  useLaunchDarklyToolbar({
    enabled: __DEV__,
    position: 'bottom-right',
    flagOverridePlugin,
    eventInterceptionPlugin,
    devServerUrl: __LOCAL_MODE__ ? dogfoodBaseUri() : undefined,
    baseUrl: 'https://app.ld.catamorphic.com',
    authUrl: 'https://integrations.ld.catamorphic.com',
    clientSideId: __LOCAL_MODE__ ? undefined : clientSideId(),
    projectKey: __LOCAL_MODE__ ? clientSideId() : undefined,
  });

  return (
    <ReactQueryClientProvider>
      <ReduxProvider store={props.reduxStore}>
        <IconProvider>
          <ThemeProvider>
            <LocaleProvider canSelectLocale={enableLanguagePicker()}>
              <DisabledTooltipTriggerContext value={enablePerceivableDisabledTooltipTriggers()}>
                {content}
              </DisabledTooltipTriggerContext>
            </LocaleProvider>
          </ThemeProvider>
        </IconProvider>
      </ReduxProvider>
    </ReactQueryClientProvider>
  );
}

export function AppProviders(props: AppProvidersProps) {
  return (
    <NoisyErrorBoundary severity="critical">
      <AppProvidersBase {...props} />
    </NoisyErrorBoundary>
  );
}
